import type { Signal } from "@preact/signals";
import {
  IcoChevronDown,
  IcoRefresh,
  IcoUndo,
  IcoUser,
  IcoUsers,
} from "../../../../components/icons";
import type { MemberOption } from "../../../../components/ui/member-picker";
import type { Group } from "../../../../types";
import { linkifyHtml } from "../../../../utils/linkify";
import { QUESTION_CATEGORIES } from "./frequency-section";
import { QuestionEditor } from "./question-editor";
import { QuestionTypeBadge } from "./question-type-badge";
import type { Question } from "./types";

export type QuestionViewMode = "grouped" | "flat";

export type QueueItemStat = {
  questionId: string;
  showCount: number;
  lastShownAt: string | Date | null | undefined;
};

type QuestionListProps = {
  questions: Question[];
  editingId: Signal<string | null>;
  groups: Group[];
  members: MemberOption[];
  viewMode: QuestionViewMode;
  queueItemStats?: QueueItemStat[];
  onSave: (id: string, updates: Partial<Question>) => void;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
};

export function QuestionList({
  questions,
  editingId,
  groups,
  members,
  viewMode,
  queueItemStats,
  onSave,
  onArchive,
  onRestore,
}: QuestionListProps) {
  // Count queued questions for the frequency section hint
  const queuedCount = questions.filter(
    (q) => !q.archived && q.frequency === "queued",
  ).length;

  // Build a map from questionId to queue stats for quick lookup
  const statsMap = new Map<string, QueueItemStat>();
  if (queueItemStats) {
    for (const item of queueItemStats) {
      statsMap.set(item.questionId, item);
    }
  }

  if (viewMode === "grouped") {
    return (
      <GroupedQuestionList
        questions={questions}
        editingId={editingId}
        groups={groups}
        members={members}
        queuedCount={queuedCount}
        queueStatsMap={statsMap}
        onSave={onSave}
        onArchive={onArchive}
        onRestore={onRestore}
      />
    );
  }

  return (
    <FlatQuestionList
      questions={questions}
      editingId={editingId}
      groups={groups}
      members={members}
      queuedCount={queuedCount}
      queueStatsMap={statsMap}
      onSave={onSave}
      onArchive={onArchive}
      onRestore={onRestore}
    />
  );
}

function FlatQuestionList({
  questions,
  editingId,
  groups,
  members,
  queuedCount,
  queueStatsMap,
  onSave,
  onArchive,
  onRestore,
}: Omit<QuestionListProps, "viewMode"> & {
  queuedCount: number;
  queueStatsMap: Map<string, QueueItemStat>;
}) {
  return (
    <div class="flex flex-col gap-2">
      {questions.map((question, index) => (
        <QuestionItem
          key={question.id}
          question={question}
          index={index}
          editingId={editingId}
          groups={groups}
          members={members}
          queuedCount={queuedCount}
          queueStat={queueStatsMap.get(question.id)}
          onSave={onSave}
          onArchive={onArchive}
          onRestore={onRestore}
        />
      ))}
    </div>
  );
}

function GroupedQuestionList({
  questions,
  editingId,
  groups,
  members,
  queuedCount,
  queueStatsMap,
  onSave,
  onArchive,
  onRestore,
}: Omit<QuestionListProps, "viewMode"> & {
  queuedCount: number;
  queueStatsMap: Map<string, QueueItemStat>;
}) {
  // Build category groups preserving original indices
  const categoryGroups = buildCategoryGroups(questions);

  return (
    <div class="flex flex-col gap-5">
      {categoryGroups.map(({ label, items }) => (
        <div key={label}>
          <div class="flex items-center gap-2 mb-3 pb-1.5 border-b border-border-muted">
            <span class="text-sm" aria-hidden="true">
              {getCategoryIcon(label)}
            </span>
            <h3 class="text-sm font-semibold text-text">{label}</h3>
            <span class="text-xs text-text-subtle font-medium">
              {items.length}
            </span>
          </div>
          <div class="flex flex-col gap-2">
            {items.map(({ question, index }) => (
              <QuestionItem
                key={question.id}
                question={question}
                index={index}
                editingId={editingId}
                groups={groups}
                members={members}
                queuedCount={queuedCount}
                queueStat={queueStatsMap.get(question.id)}
                onSave={onSave}
                onArchive={onArchive}
                onRestore={onRestore}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Groups questions by category, preserving original indices for numbering */
function buildCategoryGroups(questions: Question[]) {
  // Collect questions with their original indices
  const indexed = questions.map((question, index) => ({ question, index }));

  // "Every check-in" and "One time" questions go in a fixed group
  const everyCheckin = indexed.filter(
    (q) => !q.question.archived && q.question.frequency === "every",
  );
  const oneTime = indexed.filter(
    (q) => !q.question.archived && q.question.frequency === "one_time",
  );
  const archived = indexed.filter((q) => q.question.archived);

  // Queued questions grouped by category
  const queued = indexed.filter(
    (q) => !q.question.archived && q.question.frequency === "queued",
  );
  const categoryMap = new Map<
    string,
    { question: Question; index: number }[]
  >();
  const uncategorized: { question: Question; index: number }[] = [];

  for (const item of queued) {
    const cat = item.question.category;
    if (cat) {
      const existing = categoryMap.get(cat);
      if (existing) {
        existing.push(item);
      } else {
        categoryMap.set(cat, [item]);
      }
    } else {
      uncategorized.push(item);
    }
  }

  const result: {
    label: string;
    items: { question: Question; index: number }[];
  }[] = [];

  if (everyCheckin.length > 0) {
    result.push({ label: "Every Check-in", items: everyCheckin });
  }

  // Category groups in canonical order
  for (const cat of QUESTION_CATEGORIES) {
    const items = categoryMap.get(cat);
    if (items && items.length > 0) {
      result.push({ label: cat, items });
    }
  }

  // Any categories not in the predefined list
  for (const [cat, items] of categoryMap) {
    if (!(QUESTION_CATEGORIES as readonly string[]).includes(cat)) {
      result.push({ label: cat, items });
    }
  }

  if (uncategorized.length > 0) {
    result.push({ label: "In Rotation (No Category)", items: uncategorized });
  }

  if (oneTime.length > 0) {
    result.push({ label: "One Time", items: oneTime });
  }

  if (archived.length > 0) {
    result.push({ label: "Archived", items: archived });
  }

  return result;
}

/** Map category labels to icons */
function getCategoryIcon(label: string): string {
  const icons: Record<string, string> = {
    "Every Check-in": "\u{1F504}", // 🔄
    "AI & Innovation": "\u{1F4A1}", // 💡
    "Team & People": "\u{1F465}", // 👥
    "Projects & Results": "\u{1F4CA}", // 📊
    "Growth & Support": "\u{1F331}", // 🌱
    Leadership: "\u{1F3AF}", // 🎯
    "Wellbeing & Reflection": "\u{2764}\u{FE0F}", // ❤️
    "Company & Culture": "\u{1F3E2}", // 🏢
    "In Rotation (No Category)": "\u{1F500}", // 🔀
    "One Time": "\u{261D}\u{FE0F}", // ☝️
    Archived: "\u{1F4E6}", // 📦
  };
  return icons[label] ?? "\u{1F4CB}"; // 📋 fallback
}

function QuestionItem({
  question,
  index,
  editingId,
  groups,
  members,
  queuedCount,
  queueStat,
  onSave,
  onArchive,
  onRestore,
}: {
  question: Question;
  index: number;
  editingId: Signal<string | null>;
  groups: Group[];
  members: MemberOption[];
  queuedCount: number;
  queueStat?: QueueItemStat;
  onSave: (id: string, updates: Partial<Question>) => void;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
}) {
  const isEditing = editingId.value === question.id;
  const otherQueuedCount =
    question.frequency === "queued" ? queuedCount - 1 : queuedCount;

  if (question.archived) {
    return (
      <ArchivedQuestionCard
        question={question}
        index={index}
        onRestore={() => onRestore(question.id)}
      />
    );
  }

  if (isEditing) {
    return (
      <QuestionEditor
        question={question}
        index={index}
        groups={groups}
        members={members}
        queuedCount={otherQueuedCount}
        onUpdate={(updates) => onSave(question.id, updates)}
        onArchive={() => onArchive(question.id)}
        onClose={() => {
          editingId.value = null;
        }}
      />
    );
  }

  return (
    <QuestionCard
      question={question}
      index={index}
      queueStat={queueStat}
      onClick={() => {
        editingId.value = question.id;
      }}
    />
  );
}

function QuestionCard({
  question,
  index,
  queueStat,
  onClick,
}: {
  question: Question;
  index: number;
  queueStat?: QueueItemStat;
  onClick: () => void;
}) {
  const hasTitle = question.title.trim().length > 0;

  // Format "last shown" date for rotation questions
  const lastShownLabel = (() => {
    if (!queueStat || question.frequency !== "queued") return null;
    if (!queueStat.lastShownAt) return "Not yet shown";
    const d = new Date(queueStat.lastShownAt);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
    if (diffDays === 0) return "Shown today";
    if (diffDays === 1) return "Shown yesterday";
    if (diffDays < 7) return `Shown ${diffDays}d ago`;
    return `Shown ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  })();

  return (
    <button
      type="button"
      onClick={onClick}
      class="w-full text-left p-4 rounded-xl border border-border bg-surface hover:border-text-subtle transition-colors"
    >
      <div class="flex items-start gap-3">
        <span class="text-sm font-medium text-text-muted mt-0.5">
          {index + 1}.
        </span>
        <div class="flex-1 min-w-0">
          <div class="flex items-start gap-2">
            {hasTitle ? (
              <div
                class="font-medium text-text flex-1 min-w-0 [&>p]:m-0"
                dangerouslySetInnerHTML={{ __html: question.title }}
              />
            ) : (
              <span class="font-medium text-text-muted flex-1">
                Untitled question
              </span>
            )}
            <QuestionTypeBadge
              type={question.type}
              ratingMax={question.ratingMax}
            />
          </div>
          {question.description && (
            <div
              class="text-sm text-text-muted mt-1 line-clamp-2 [&>p]:m-0 [&_a]:text-[var(--brand-600)] [&_a]:underline"
              dangerouslySetInnerHTML={{
                __html: linkifyHtml(question.description),
              }}
            />
          )}
          <div class="flex flex-wrap items-center gap-1.5 mt-1.5">
            {question.required && <RequiredBadge />}
            <TargetingBadge
              targetType={question.targetType}
              targetIds={question.targetIds}
            />
            <FrequencyBadge
              frequency={question.frequency}
              category={question.category}
            />
            {lastShownLabel && (
              <span
                class="text-xs text-text-subtle"
                title={`Shown ${queueStat?.showCount ?? 0} time(s) total`}
              >
                {lastShownLabel} ({queueStat?.showCount ?? 0}x)
              </span>
            )}
          </div>
        </div>
        <IcoChevronDown class="size-5 text-text-muted flex-shrink-0 mt-0.5" />
      </div>
    </button>
  );
}

function TargetingBadge({
  targetType,
  targetIds,
}: {
  targetType: Question["targetType"];
  targetIds: string[];
}) {
  if (targetType === "company_wide") return null;

  const count = targetIds.length;
  if (count === 0) return null;

  const label =
    targetType === "group"
      ? `${count} ${count === 1 ? "group" : "groups"}`
      : `${count} ${count === 1 ? "person" : "people"}`;

  const Icon = targetType === "group" ? IcoUsers : IcoUser;

  return (
    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-xs text-blue-700 dark:text-blue-300 font-medium">
      <Icon class="size-3" />
      {label}
    </span>
  );
}

function FrequencyBadge({
  frequency,
  category,
}: {
  frequency: Question["frequency"];
  category?: string | null;
}) {
  if (frequency === "every") return null;

  const label =
    frequency === "queued"
      ? category
        ? `In rotation: ${category}`
        : "In rotation"
      : "One time";

  return (
    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-surface-hover text-xs text-text-muted">
      <IcoRefresh class="size-3" />
      {label}
    </span>
  );
}

function RequiredBadge() {
  return (
    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-50 dark:bg-rose-900/20 text-xs text-rose-600 dark:text-rose-400">
      Required
    </span>
  );
}

function ArchivedQuestionCard({
  question,
  index,
  onRestore,
}: {
  question: Question;
  index: number;
  onRestore: () => void;
}) {
  const hasTitle = question.title.trim().length > 0;

  return (
    <div class="flex items-center gap-3 p-4 rounded-xl border border-border bg-surface-muted opacity-60">
      <span class="text-sm font-medium text-text-muted">{index + 1}.</span>
      {hasTitle ? (
        <span
          class="flex-1 text-text-muted truncate [&>p]:m-0 [&>p]:inline"
          dangerouslySetInnerHTML={{ __html: question.title }}
        />
      ) : (
        <span class="flex-1 text-text-muted truncate">Untitled question</span>
      )}
      <button
        type="button"
        onClick={onRestore}
        class="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
      >
        <IcoUndo class="size-4" />
        Restore
      </button>
    </div>
  );
}
